import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ForceGraph from 'force-graph';
import { makeStarfield } from './starfield.js';
import { makeShips } from './ships.js';
import { isCanvasPath, isMarkdownPath } from '@rbv/shared';
import { index } from '../worker/client.js';
import type { GraphData, GraphFilter } from '../worker/api.js';
import { vault } from '../sync/vault.js';
import { DepthStepper, GraphControls } from './controls.jsx';
import {
  clamp,
  endId,
  nodeRadius,
  readThemeColors,
  withAlpha,
  type FG,
  type GLink,
  type GNode,
  type ThemeColors,
} from './internal.js';
import { KIND_STYLE, kindOf, readKindColors, type MoKind } from './kinds.js';
import { groupColorFor, parseGroups, type GraphGroup, type ParsedGroup } from './matcher.js';
import {
  GLOBAL_GRAPH_SETTINGS_KEY,
  LOCAL_GRAPH_SETTINGS_KEY,
  loadGraphSettings,
  saveGraphSettings,
  type GraphSettings,
} from './settings.js';
import { Timelapse } from './timelapse.js';
import { Graph3D } from './Graph3D.jsx';
import './graph.css';
import { Icon } from '../mo/Icon.js';

/**
 * GraphView — Obsidian-parity force graph (force-graph v1, canvas).
 *  - mode 'global': whole vault; mode 'local': BFS neighborhood of noteId
 *    with a depth control (1-3).
 *  - Data from index.graph()/localGraph(), re-fetched (debounced 500ms) on
 *    index.onChange; node objects are reused across refreshes so the
 *    simulation keeps its positions.
 *  - Physics: d3AlphaDecay .0228 / d3VelocityDecay .25, four force sliders
 *    (center/repel/link/link distance) persisted in localStorage.
 *  - Hover/tap highlights node + neighbors, fades the rest; labels fade in
 *    with zoom (text fade threshold slider); color groups from
 *    vault.settings.graphGroups (first match wins).
 *  - Timelapse (global mode): replays vault growth in ctime order.
 */
export interface GraphViewProps {
  mode: 'global' | 'local';
  noteId?: string;
  onOpenNote: (id: string) => void;
}

/**
 * TWO CHARTS, ONE ENTRY POINT.
 *
 * He asked for the knowledge graph in 3D so he can grab and rotate it. Global mode therefore opens
 * in 3D, and the flat chart is one press away because it is still the only place the timelapse,
 * the four force sliders and the colour groups live. Local mode stays flat: a depth-limited
 * neighbourhood of one note is a small diagram, and spinning it would make it harder to read
 * rather than easier.
 *
 * The choice is REMEMBERED, because a view preference he has to re-make on every visit is a view
 * preference the app is overriding. Read defensively: private mode throws on localStorage, and a
 * blank graph is a worse outcome than a forgotten toggle.
 */
const GRAPH_DIM_KEY = 'mo-os:graph-dimensions';

function preferred3D(): boolean {
  try {
    return globalThis.localStorage?.getItem(GRAPH_DIM_KEY) !== '2d';
  } catch {
    return true;
  }
}

export function GraphView(props: GraphViewProps) {
  const [threeD, setThreeD] = useState<boolean>(() => props.mode === 'global' && preferred3D());

  const choose = useCallback((next: boolean): void => {
    setThreeD(next);
    try {
      globalThis.localStorage?.setItem(GRAPH_DIM_KEY, next ? '3d' : '2d');
    } catch {
      // Same reasoning as the read: not worth failing a view over.
    }
  }, []);

  if (props.mode === 'global' && threeD) {
    return <Graph3D onOpenNote={props.onOpenNote} onExit={() => choose(false)} />;
  }
  return (
    <GraphInner
      mode={props.mode}
      noteId={props.noteId}
      onOpenNote={props.onOpenNote}
      on3D={props.mode === 'global' ? () => choose(true) : undefined}
    />
  );
}

/** Compact local graph (~300px) for embedding in the right sidebar. */
export function LocalGraphPanel(props: { noteId: string; onOpenNote: (id: string) => void }) {
  return (
    <div className="graph-local-panel">
      <div className="graph-local-panel-title">Graph</div>
      <GraphInner mode="local" noteId={props.noteId} onOpenNote={props.onOpenNote} compact />
    </div>
  );
}

// ---------------------------------------------------------------------------

interface InnerProps extends GraphViewProps {
  compact?: boolean;
  /** Present only in global mode: the way into the 3D chart. */
  on3D?: () => void;
}

interface HoverState {
  id: string;
  neighbors: Set<string>;
}

const safeTitle = (id: string): string => {
  try {
    return vault.titleOf(id);
  } catch {
    return '';
  }
};

function useGraphGroups(): GraphGroup[] {
  const [groups, setGroups] = useState<GraphGroup[]>(() => {
    try {
      return vault.settings.graphGroups ?? [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      const map = vault.settingsMap;
      const handler = () => {
        setGroups(vault.settings.graphGroups ?? []);
      };
      map.observe(handler);
      return () => map.unobserve(handler);
    } catch {
      return undefined; // vault not started (shouldn't happen post-boot)
    }
  }, []);
  return groups;
}

function GraphInner({ mode, noteId, onOpenNote, compact = false, on3D }: InnerProps) {
  const storageKey = mode === 'local' ? LOCAL_GRAPH_SETTINGS_KEY : GLOBAL_GRAPH_SETTINGS_KEY;
  const [settings, setSettings] = useState<GraphSettings>(() => loadGraphSettings(storageKey));
  const [query, setQuery] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [tl, setTl] = useState({ playing: false, progress: 0 });
  const [isEmpty, setIsEmpty] = useState(false);
  const groups = useGraphGroups();
  const parsedGroups = useMemo(() => parseGroups(groups), [groups]);
  /*
   * How many nodes of each kind are on screen, for the legend. Counted where the data is applied
   * rather than in the draw loop, which runs per node per frame.
   */
  const [kindCounts, setKindCounts] = useState<Partial<Record<MoKind, number>>>({});

  const containerRef = useRef<HTMLDivElement>(null);
  const fgRef = useRef<FG | null>(null);
  /*
   * The node count, captured when the data is set rather than read per frame.
   *
   * THE BUG THIS FIXES, reported 2026-08-27 as the graph being static. The ambient layer needed the
   * count to size its budget, and I read it with `fg.graphData().nodes.length` inside
   * `onRenderFramePre`. `graphData` is a KAPSULE PROP, not a plain getter: calling it re-enters the
   * component's state system on every single frame, which retriggers layout work and leaves the
   * simulation unable to settle or move. It typechecks, it throws nothing, and it freezes the graph.
   *
   * Nothing in a render callback may call a force-graph configuration method. Read a ref instead.
   */
  const nodeCountRef = useRef(0);
  const colorsRef = useRef<ThemeColors | null>(null);
  /*
   * Kind colours, resolved from the theme exactly once per theme change rather than per draw.
   * `readKindColors` calls getComputedStyle, and a draw call that touched the DOM would do it
   * once per node per frame.
   */
  const kindColorsRef = useRef<Record<MoKind, string>>(readKindColors());
  const byIdRef = useRef<Map<string, GNode>>(new Map());
  const fullRef = useRef<{ nodes: GNode[]; links: GLink[] }>({ nodes: [], links: [] });
  const neighborsRef = useRef<Map<string, Set<string>>>(new Map());
  const hoverRef = useRef<HoverState | null>(null);
  const timelapseRef = useRef<Timelapse | null>(null);
  const pendingRefreshRef = useRef(false);
  const didFitRef = useRef(false);
  const isTouchRef = useRef(false);
  const lastBgClickRef = useRef(0);
  const defaultLinkStrengthRef = useRef<((l: GLink, i: number, links: GLink[]) => number) | null>(
    null,
  );
  const seqRef = useRef(0);

  // Latest values readable from stable callbacks.
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const queryRef = useRef(query);
  queryRef.current = query;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const noteIdRef = useRef(noteId);
  noteIdRef.current = noteId;
  const onOpenNoteRef = useRef(onOpenNote);
  onOpenNoteRef.current = onOpenNote;
  const parsedGroupsRef = useRef<ParsedGroup[]>(parsedGroups);
  parsedGroupsRef.current = parsedGroups;
  const centerIdRef = useRef<string | undefined>(undefined);
  centerIdRef.current = mode === 'local' ? noteId : undefined;
  const fitPadRef = useRef(compact ? 16 : 60);
  fitPadRef.current = compact ? 16 : 60;

  // ------------------------------------------------------------ persistence

  useEffect(() => {
    setSettings(loadGraphSettings(storageKey));
  }, [storageKey]);

  useEffect(() => {
    saveGraphSettings(storageKey, settings);
  }, [storageKey, settings]);

  const patchSettings = useCallback((patch: Partial<GraphSettings>) => {
    setSettings((s) => ({ ...s, ...patch }));
  }, []);

  const setGroups = useCallback((next: GraphGroup[]) => {
    try {
      vault.setSetting('graphGroups', next);
    } catch {
      // vault not started; nothing to persist to
    }
  }, []);

  // -------------------------------------------------------------- rendering

  const setHover = useCallback((node: GNode | null) => {
    hoverRef.current = node
      ? { id: node.id, neighbors: neighborsRef.current.get(node.id) ?? new Set() }
      : null;
  }, []);

  const drawNode = useCallback((node: GNode, ctx: CanvasRenderingContext2D, scale: number) => {
    const colors = colorsRef.current;
    if (!colors) return;
    const hover = hoverRef.current;
    const isHover = hover?.id === node.id;
    const inHood = !hover || isHover || hover.neighbors.has(node.id);
    const isCenter = node.id === centerIdRef.current;
    const x = node.x ?? 0;
    const y = node.y ?? 0;
    const r = node.radius;

    /*
     * COLOURED BY WHAT THE NOTE IS, the same scheme and the same tokens as the 3D view.
     *
     * Before this the 2D graph had one accent for everything, so a person, an org and a
     * commitment were indistinguishable dots while the 3D view coloured them. Same vault, two
     * visual languages, depending only on which button you pressed.
     *
     * His own colour GROUPS still win. Those are a deliberate configuration he wrote, and a
     * derived kind colour must never overrule something he set by hand. The centre no longer
     * takes the accent either: it already carries a ring (below), so it can keep its identity
     * rather than trade it for position.
     */
    const fill = node.gcolor ?? kindColorsRef.current[kindOf(node)];
    let alpha = inHood ? 1 : 0.08;
    if (node.kind === 'phantom') alpha *= 0.45;
    else if (node.kind === 'attachment' && !node.gcolor) alpha *= 0.75;

    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, 2 * Math.PI);
    ctx.fillStyle = fill;
    ctx.fill();
    if (isHover || (isCenter && !hover)) {
      ctx.strokeStyle = colors.accentStrong;
      ctx.lineWidth = 1.5 / scale;
      ctx.stroke();
    }

    // Label under the dot, alpha ramped by zoom (threshold slider) + hover.
    const threshold = Math.pow(2, settingsRef.current.textFade);
    let labelAlpha = clamp((scale / threshold - 0.8) / 0.7, 0, 1);
    if (hover) labelAlpha = inHood ? Math.max(labelAlpha, isHover ? 1 : 0.9) : 0;
    if (node.kind === 'phantom') labelAlpha *= 0.7;
    if (labelAlpha > 0.02) {
      const screenPx = 10 * Math.pow(clamp(scale, 1, 4), 0.35);
      ctx.globalAlpha = labelAlpha;
      ctx.font = `${screenPx / scale}px ${colors.fontUi}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = colors.text;
      ctx.fillText(node.title, x, y + r + 1.5 + 2 / scale);
    }
    ctx.globalAlpha = 1;
  }, []);

  const paintPointerArea = useCallback(
    (node: GNode, color: string, ctx: CanvasRenderingContext2D, scale: number) => {
      // Keep a usable hit target (~28px screen) at any zoom level.
      const r = Math.max(node.radius + 2, 14 / scale);
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(node.x ?? 0, node.y ?? 0, r, 0, 2 * Math.PI);
      ctx.fill();
    },
    [],
  );

  const linkColor = useCallback((l: GLink): string => {
    const colors = colorsRef.current;
    if (!colors) return 'rgba(128,128,128,0.3)';
    const h = hoverRef.current;
    if (!h) return withAlpha(colors.line, 0.32);
    if (endId(l.source) === h.id || endId(l.target) === h.id) return colors.accent;
    return withAlpha(colors.line, 0.05);
  }, []);

  const linkWidth = useCallback((l: GLink): number => {
    const h = hoverRef.current;
    return h && (endId(l.source) === h.id || endId(l.target) === h.id) ? 1.2 : 0.6;
  }, []);

  // ------------------------------------------------------------------- data

  const applyGroupColors = useCallback(() => {
    for (const n of byIdRef.current.values()) {
      n.gcolor = groupColorFor(n, parsedGroupsRef.current);
    }
  }, []);

  const applyData = useCallback(
    (data: GraphData) => {
      const fg = fgRef.current;
      if (!fg) return;
      const prev = byIdRef.current;
      const byId = new Map<string, GNode>();
      const nodes: GNode[] = [];
      for (const n of data.nodes) {
        // Reuse existing node objects (by id) so positions/velocities carry
        // over and the simulation never restarts from scratch.
        let g = prev.get(n.id);
        if (g) {
          g.path = n.path;
          g.title = n.title;
          g.degree = n.degree;
          g.ctime = n.ctime;
          g.tags = n.tags;
          g.unresolved = n.unresolved;
        } else {
          g = { ...n, kind: 'note', radius: 0 };
        }
        g.kind = n.unresolved
          ? 'phantom'
          : isMarkdownPath(n.path) || isCanvasPath(n.path)
            ? 'note'
            : 'attachment';
        g.radius = nodeRadius(n.degree, g.kind);
        byId.set(g.id, g);
        nodes.push(g);
      }
      const links: GLink[] = data.links.map((l) => ({ source: l.source, target: l.target }));
      const neighbors = new Map<string, Set<string>>();
      const addNeighbor = (a: string, b: string) => {
        let s = neighbors.get(a);
        if (!s) neighbors.set(a, (s = new Set()));
        s.add(b);
      };
      for (const l of data.links) {
        addNeighbor(l.source, l.target);
        addNeighbor(l.target, l.source);
      }
      byIdRef.current = byId;
      neighborsRef.current = neighbors;
      fullRef.current = { nodes, links };
      applyGroupColors();
      // Re-anchor (or drop) the hover state on the fresh adjacency.
      const h = hoverRef.current;
      if (h) {
        hoverRef.current = byId.has(h.id)
          ? { id: h.id, neighbors: neighbors.get(h.id) ?? new Set() }
          : null;
      }
      setIsEmpty(nodes.length === 0);
      const counts: Partial<Record<MoKind, number>> = {};
      for (const n of nodes) {
        const k = kindOf(n);
        counts[k] = (counts[k] ?? 0) + 1;
      }
      setKindCounts(counts);
      fg.graphData({ nodes, links });
      // Captured HERE, not read in the render loop. See the note at onRenderFramePre.
      nodeCountRef.current = nodes.length;
      if (!didFitRef.current && nodes.length > 0) {
        didFitRef.current = true;
        window.setTimeout(() => fgRef.current?.zoomToFit(400, fitPadRef.current), 600);
      }
    },
    [applyGroupColors],
  );

  const refresh = useCallback(async () => {
    if (!fgRef.current) return;
    if (timelapseRef.current) {
      pendingRefreshRef.current = true;
      return;
    }
    const seq = ++seqRef.current;
    const s = settingsRef.current;
    const filter: GraphFilter = {
      query: queryRef.current.trim() || undefined,
      showOrphans: s.showOrphans,
      showUnresolved: s.showUnresolved,
      showAttachments: s.showAttachments,
    };
    let data: GraphData;
    if (modeRef.current === 'local') {
      const id = noteIdRef.current;
      data = id ? await index.localGraph(id, s.depth, filter) : { nodes: [], links: [] };
    } else {
      data = await index.graph(filter);
    }
    if (seq !== seqRef.current || !fgRef.current) return;
    if (timelapseRef.current) {
      pendingRefreshRef.current = true;
      return;
    }
    applyData(data);
  }, [applyData]);

  // -------------------------------------------------------------- timelapse

  const cancelTimelapse = useCallback(() => {
    timelapseRef.current?.stop();
  }, []);

  const startTimelapse = useCallback(() => {
    const fg = fgRef.current;
    if (!fg || timelapseRef.current) return;
    const timelapse = new Timelapse(
      fg,
      fullRef.current,
      (p) => setTl({ playing: true, progress: p }),
      () => {
        timelapseRef.current = null;
        setTl({ playing: false, progress: 0 });
        if (pendingRefreshRef.current) {
          pendingRefreshRef.current = false;
          void refresh();
        }
      },
    );
    if (!timelapse.start()) return;
    timelapseRef.current = timelapse;
    setPanelOpen(false);
    setTl({ playing: true, progress: 0 });
  }, [refresh]);

  const onCanvasInteraction = useCallback(() => {
    if (timelapseRef.current) cancelTimelapse();
  }, [cancelTimelapse]);

  // --------------------------------------------------------------- lifecycle

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    isTouchRef.current = window.matchMedia('(pointer: coarse)').matches;
    colorsRef.current = readThemeColors();
    kindColorsRef.current = readKindColors();

    const fg = new ForceGraph<GNode, GLink>(el);
    fgRef.current = fg;
    /*
     * THE FIELD THE CHART SITS IN. Drawn before the links and nodes, so nothing it contains is ever
     * on top of something he is reading, which is the first of the plan's motion rules. It rides
     * the loop force-graph is already running for the hover highlight, so it adds frames to nothing.
     */
    const starfield = makeStarfield(el);
    /*
     * Traffic across the chart, behind the nodes and in screen space. The node count is passed in
     * on every frame rather than captured, because it changes as he filters and the budget has to
     * follow: above a few thousand nodes the force layout is the expensive thing and the scenery
     * gives way to it.
     */
    const ships = makeShips();

    fg.backgroundColor('rgba(0,0,0,0)')
      .onRenderFramePre((ctx, globalScale) => {
        starfield.draw(ctx, globalScale);
        ships.draw(ctx, nodeCountRef.current);
      })
      .nodeId('id')
      .nodeLabel(() => '') // labels are drawn on canvas, no tooltip
      .autoPauseRedraw(false) // hover highlight must repaint after settle
      .d3AlphaDecay(0.0228)
      .d3VelocityDecay(0.25)
      .nodeRelSize(1)
      .nodeCanvasObject(drawNode)
      .nodePointerAreaPaint(paintPointerArea)
      .linkColor(linkColor)
      .linkWidth(linkWidth)
      .minZoom(0.05)
      .maxZoom(8)
      .onNodeHover((node) => {
        if (!isTouchRef.current) setHover(node);
      })
      .onNodeClick((node) => {
        if (timelapseRef.current) {
          cancelTimelapse();
          return;
        }
        // Touch: first tap highlights the neighborhood, second tap opens.
        if (isTouchRef.current && hoverRef.current?.id !== node.id) {
          setHover(node);
          return;
        }
        if (node.kind === 'phantom') return; // never create from the graph
        onOpenNoteRef.current(node.id);
      })
      .onNodeDragEnd((node) => {
        // Release un-pins the node so it settles back into the layout.
        node.fx = undefined;
        node.fy = undefined;
      })
      .onBackgroundClick(() => {
        if (isTouchRef.current) setHover(null);
        const now = Date.now();
        if (now - lastBgClickRef.current < 350) {
          lastBgClickRef.current = 0;
          fgRef.current?.zoomToFit(400, fitPadRef.current);
        } else {
          lastBgClickRef.current = now;
        }
      });

    // Capture d3's default per-link strength (1/min(degree)) so the link
    // force slider scales it instead of replacing it.
    const linkForce = fg.d3Force('link');
    const defStrength = linkForce?.strength?.();
    if (typeof defStrength === 'function') {
      defaultLinkStrengthRef.current = defStrength as (
        l: GLink,
        i: number,
        links: GLink[],
      ) => number;
    }

    const resize = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) fg.width(rect.width).height(rect.height);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(el);

    // Stop the render loop (and d3 ticks) whenever the graph is off-screen —
    // e.g. the local-graph panel translated off-canvas on mobile behind an
    // open note, or a hidden tab — so it doesn't repaint every frame forever.
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!fgRef.current) return;
        if (entry.isIntersecting) fgRef.current.resumeAnimation();
        else fgRef.current.pauseAnimation();
      },
      { threshold: 0 },
    );
    io.observe(el);
    const onVisibility = () => {
      if (!fgRef.current) return;
      if (document.hidden) fgRef.current.pauseAnimation();
      else fgRef.current.resumeAnimation();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onTheme = () => {
      colorsRef.current = readThemeColors();
      kindColorsRef.current = readKindColors();
    kindColorsRef.current = readKindColors();
    };
    mq.addEventListener('change', onTheme);

    return () => {
      ro.disconnect();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      mq.removeEventListener('change', onTheme);
      timelapseRef.current?.dispose();
      timelapseRef.current = null;
      fg._destructor();
      fgRef.current = null;
    };
    // Static wiring: all handlers are stable callbacks reading refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply the four force sliders (Obsidian/Quartz-style mapping) + reheat.
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    const charge = fg.d3Force('charge');
    charge?.strength(-30 * settings.repelForce);
    const center = fg.d3Force('center');
    center?.strength?.(settings.centerForce);
    const link = fg.d3Force('link');
    if (link) {
      link.distance(settings.linkDistance * 0.4);
      const def = defaultLinkStrengthRef.current;
      if (def) {
        link.strength((l: GLink, i: number, links: GLink[]) => def(l, i, links) * settings.linkForce);
      } else {
        link.strength(settings.linkForce);
      }
    }
    fg.d3ReheatSimulation();
  }, [settings.centerForce, settings.repelForce, settings.linkForce, settings.linkDistance]);

  // Re-fetch when the filter inputs change (debounced for typing).
  useEffect(() => {
    const t = window.setTimeout(() => void refresh(), 250);
    return () => window.clearTimeout(t);
  }, [
    query,
    settings.showOrphans,
    settings.showUnresolved,
    settings.showAttachments,
    settings.depth,
    mode,
    noteId,
    refresh,
  ]);

  // Re-fetch on index changes (debounced 500ms).
  useEffect(() => {
    let t = 0;
    const unsub = index.onChange(() => {
      window.clearTimeout(t);
      t = window.setTimeout(() => void refresh(), 500);
    });
    return () => {
      unsub();
      window.clearTimeout(t);
    };
  }, [refresh]);

  // Recolor in place when groups change (canvas picks it up next frame).
  useEffect(() => {
    applyGroupColors();
  }, [parsedGroups, applyGroupColors]);

  // Re-fit when the local graph re-centers on another note.
  useEffect(() => {
    if (mode === 'local') didFitRef.current = false;
  }, [mode, noteId]);

  // ------------------------------------------------------------------ render

  return (
    <div className={`graph-view${compact ? ' compact' : ''}`}>
      <div
        ref={containerRef}
        className="graph-canvas"
        onPointerDownCapture={onCanvasInteraction}
        onWheelCapture={onCanvasInteraction}
      />

      {mode === 'local' && !compact && noteId && (
        <div className="graph-local-header">
          <span className="graph-local-title">{safeTitle(noteId)}</span>
          <DepthStepper
            small
            value={settings.depth}
            onChange={(d) => patchSettings({ depth: d })}
          />
        </div>
      )}

      {compact && mode === 'local' && (
        <div className="graph-compact-depth">
          <DepthStepper
            small
            value={settings.depth}
            onChange={(d) => patchSettings({ depth: d })}
          />
        </div>
      )}

      {/* The way back into 3D. In the same corner the 3D chart puts its own "2D", so the two
          views swap in place rather than moving the control he just pressed. */}
      {on3D !== undefined && !compact && (
        <div className="g3-hud">
          <button type="button" className="g3-btn" onClick={on3D} title="The 3D star chart: grab it and rotate it">
            3D
          </button>

          {/* The same legend, the same classes and the same tokens as the 3D chart. A palette
              nobody can read means nothing, and two views of one vault must not need two keys.
              Only kinds actually on screen are listed. */}
          <ul className="g3-legend">
            {(Object.keys(KIND_STYLE) as MoKind[])
              .filter((k) => (kindCounts[k] ?? 0) > 0)
              .map((k) => (
                <li key={k}>
                  <span className="g3-swatch" style={{ background: `var(${KIND_STYLE[k].token})` }} />
                  {KIND_STYLE[k].label}
                  <span className="g3-n">{(kindCounts[k] ?? 0).toLocaleString()}</span>
                </li>
              ))}
          </ul>
        </div>
      )}

      {!compact && (
        <GraphControls
          mode={mode}
          open={panelOpen}
          onToggle={() => setPanelOpen((o) => !o)}
          query={query}
          onQuery={setQuery}
          settings={settings}
          onSettings={patchSettings}
          groups={groups}
          onGroups={setGroups}
          timelapsePlaying={tl.playing}
          onTimelapseStart={startTimelapse}
        />
      )}

      {tl.playing && (
        <div className="graph-timelapse-bar">
          <input
            type="range"
            min={0}
            max={1000}
            value={Math.round(tl.progress * 1000)}
            aria-label="Timelapse progress"
            onChange={(e) => timelapseRef.current?.seek(Number(e.currentTarget.value) / 1000)}
          />
          <button
            className="graph-close-btn"
            aria-label="Stop timelapse"
            onClick={cancelTimelapse}
          ><Icon name="close" size={15} /></button>
        </div>
      )}

      {isEmpty && (
        <div className="graph-empty muted">
          {mode === 'local' ? 'No connections' : 'No notes to show'}
        </div>
      )}
    </div>
  );
}
